import React, { useState, useEffect } from 'react';
import api, { setToken, clearToken, getToken } from './api/client';
import AuthScreen from './pages/AuthScreen';
import Dashboard from './pages/Dashboard';
import MyTodos from './pages/MyTodos';
import MyProfile from './pages/MyProfile';
import AllTodosManagement from './pages/AllTodosManagement';
import UsersManagement from './pages/UsersManagement';
import MeetingsManagement from './pages/MeetingsManagement';
import AllTopicsManagement from './pages/AllTopicsManagement';
import MeetingDetail from './pages/MeetingDetail';
import ActiveMeetingSession from './pages/ActiveMeetingSession';
import AllSessions from './pages/AllSessions';
import ClosedTopics from './pages/ClosedTopics';
import CompletedTodos from './pages/CompletedTodos';
import Sidebar from './components/Sidebar';
import { MeetingSummaryModal } from './components/modals';
import './styles/index.css';

/**
 * Main Application Component
 */
function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('dashboard');
  const [timeframeFilter, setTimeframeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedMeeting, setSelectedMeeting] = useState(null);
  const [activeSession, setActiveSession] = useState(null);
  const [viewAllSessions, setViewAllSessions] = useState(false);
  const [viewClosedTopics, setViewClosedTopics] = useState(false);
  const [viewCompletedTodos, setViewCompletedTodos] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showSummary, setShowSummary] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = getToken();
    if (token) {
      try {
        const user = await api.get('/auth/me');
        setCurrentUser(user);
      } catch (error) {
        clearToken();
      }
    }
    setLoading(false);
  };

  const handleLogin = (user, token) => {
    setToken(token);
    setCurrentUser(user);
  };

  const handleLogout = () => {
    clearToken();
    setCurrentUser(null);
    setView('dashboard');
    setSelectedMeeting(null);
  };

  // Enhanced setView to handle optional timeframe and status parameters
  const handleSetView = (newView, timeframe = null, status = null) => {
    setView(newView);
    setTimeframeFilter(timeframe);
    setStatusFilter(status);
  };

  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return <AuthScreen onLogin={handleLogin} />;
  }

  // If there's an active session, render the session view
  if (activeSession) {
    return (
      <ActiveMeetingSession
        session={activeSession}
        meeting={selectedMeeting}
        user={currentUser}
        onEndMeeting={(endedSession) => {
          setActiveSession(null);
          setSelectedSession(endedSession);
          setShowSummary(true);
        }}
      />
    );
  }

  return (
    <div className="app-container">
      <Sidebar
        user={currentUser}
        view={view}
        setView={handleSetView}
        selectedMeeting={selectedMeeting}
        setSelectedMeeting={(meeting) => {
          setSelectedMeeting(meeting);
        }}
        onLogout={handleLogout}
      />

      <main className="content-area">
          {view === 'dashboard' && (
            <Dashboard
              user={currentUser}
              setView={handleSetView}
              setSelectedMeeting={setSelectedMeeting}
            />
          )}

          {view === 'my-todos' && (
            <MyTodos
              user={currentUser}
              initialTimeframe={timeframeFilter}
              initialStatus={statusFilter}
            />
          )}

          {view === 'my-profile' && (
            <MyProfile
              user={currentUser}
              onUpdate={() => checkAuth()}
              onCancel={() => setView('dashboard')}
            />
          )}

          {view === 'users' && currentUser.isAdmin && <UsersManagement />}

          {view === 'meetings' && (currentUser.isAdmin || currentUser.isManager) && <MeetingsManagement user={currentUser} />}

          {view === 'all-todos' && currentUser.isAdmin && (
            <AllTodosManagement
              user={currentUser}
              initialTimeframe={timeframeFilter}
            />
          )}

          {view === 'all-topics' && currentUser.isAdmin && (
            <AllTopicsManagement user={currentUser} />
          )}

          {view === 'meeting-detail' && selectedMeeting && !viewAllSessions && !viewClosedTopics && !viewCompletedTodos && (
            <MeetingDetail
              meeting={selectedMeeting}
              user={currentUser}
              setActiveSession={setActiveSession}
              setView={setView}
              onViewAllSessions={() => setViewAllSessions(true)}
              onViewClosedTopics={() => setViewClosedTopics(true)}
              onViewCompletedTodos={() => setViewCompletedTodos(true)}
              onViewSummary={() => console.log('View summary')}
            />
          )}

          {view === 'meeting-detail' && selectedMeeting && viewAllSessions && (
            <AllSessions
              meeting={selectedMeeting}
              user={currentUser}
              onBack={() => setViewAllSessions(false)}
              onViewSummary={(session) => {
                setSelectedSession(session);
                setShowSummary(true);
              }}
              onResumeSession={(session) => {
                setViewAllSessions(false);
                setActiveSession(session);
              }}
            />
          )}

          {view === 'meeting-detail' && selectedMeeting && viewClosedTopics && (
            <ClosedTopics
              meeting={selectedMeeting}
              user={currentUser}
              onBack={() => setViewClosedTopics(false)}
            />
          )}

          {view === 'meeting-detail' && selectedMeeting && viewCompletedTodos && (
            <CompletedTodos
              meeting={selectedMeeting}
              user={currentUser}
              onBack={() => setViewCompletedTodos(false)}
            />
          )}
      </main>

      {/* Meeting Summary Modal */}
      {showSummary && selectedSession && selectedMeeting && (
        <MeetingSummaryModal
          session={selectedSession}
          meeting={selectedMeeting}
          onClose={() => {
            setShowSummary(false);
            setSelectedSession(null);
            setView('meeting-detail');
          }}
        />
      )}
    </div>
  );
}

export default App;
